import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { User, UserRole } from '../../types';
import {
  canUseViewAs,
  getEffectiveRoles,
  hasEffectiveRole,
  isPreviewableRole,
  loadViewAsRole,
  saveViewAsRole,
} from '../lib/viewAsRole';

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (patch: Partial<User>) => void;
  isAuthenticated: boolean;
  isCheckingSession: boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  /** Roles used for navigation and UI gates (preview role when active). */
  effectiveRoles: UserRole[];
  viewAsRole: UserRole | null;
  isViewingAs: boolean;
  canUseViewAs: boolean;
  setViewAsRole: (role: UserRole | null) => void;
  clearViewAsRole: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_KEY = 'logistrade_user';

// Security hardening (27/07): el access/refresh token ya NO viven en localStorage ni en ninguna
// variable JS-legible -- viajan solo como cookies httpOnly (ver issueAuthCookies en
// server.ts), invisibles incluso para un XSS. `user` (nombre/email/roles, no un secreto) sigue
// cacheado en localStorage solo para poder pintar la UI de inmediato al recargar la página,
// mientras se confirma the session real contra the server (ver el useEffect de abajo) -- nunca
// is used por sí solo para decidir `isAuthenticated`.
let onSessionExpired: (() => void) | null = null;
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST' });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// Global fetch interceptor: ante un 401/403 propio, intenta un refresh silencioso y
// reintenta la petición original UNA vez before rendirse. Como el token ahora vive en una
// cookie httpOnly, ni este interceptor ni el reintento necesitan tocar headers -- el navegador
// manda la cookie (nueva, tras el refresh) automáticamente.
const originalFetch = window.fetch.bind(window);

try {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const isApiCall = url.startsWith('/api/') || url.includes('/api/');
    const isAuthCall = url.includes('/api/auth/');

    const response = await originalFetch(input, init);

    if (!isApiCall || isAuthCall || (response.status !== 401 && response.status !== 403)) {
      return response;
    }

    // A 403 can also mean "valid token, wrong role" (checkRole) rather than an
    // expired/invalid token — refreshing won't fix that, so skip it and let the
    // caller handle the permission error as-is.
    if (response.status === 403) {
      const probe = response.clone();
      try {
        const body = await probe.json();
        if (body && typeof body.error === 'string' && body.error.toLowerCase().includes('permiso')) {
          return response;
        }
      } catch {
        // Non-JSON 403 body: fall through and attempt a refresh anyway.
      }
    }

    const refreshed = await refreshSession();
    if (!refreshed) {
      onSessionExpired?.();
      return response;
    }

    return originalFetch(input, init);
  };
} catch (e) {
  console.warn("Entorno restringido: No se pudo modificar window.fetch. Usando implementación nativa.");
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [viewAsRole, setViewAsRoleState] = useState<UserRole | null>(() => loadViewAsRole());
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const initialized = useRef(false);

  useEffect(() => {
    const savedUser = localStorage.getItem(USER_KEY);
    if (savedUser) {
      // Pintado optimista: mientras se confirma the session real contra the server, evita el
      // parpadeo de "pantalla de login" en cada recarga para un usuario que sigue logueado.
      try { setUser(JSON.parse(savedUser)); } catch { /* dato corrupto, is ignored */ }
    }

    // Confirmar sesión real contra the server
    (async () => {
      try {
        const res = await originalFetch('/api/auth/session');
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
          if (data.rememberMe !== undefined) {
            localStorage.setItem('logistrade_remember_me', data.rememberMe ? '1' : '0');
          }
          localStorage.setItem('logistrade_last_activity', Date.now().toString());
        } else {
          setUser(null);
          localStorage.removeItem(USER_KEY);
        }
      } catch {
        // Sin red: conserva the user cacheado instead of forzar un logout espurio.
      } finally {
        setIsCheckingSession(false);
      }
    })();

    onSessionExpired = () => {
      setUser(null);
      localStorage.removeItem(USER_KEY);
      localStorage.setItem('logistrade_session_expired', '1');
    };
    initialized.current = true;

    return () => {
      onSessionExpired = null;
    };
  }, []);

  // Monitor de interacción del usuario para inactividad
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      localStorage.setItem('logistrade_last_activity', Date.now().toString());
    };

    // Actualizar timestamp en eventos de usuario (debounced / pasivos)
    let activityTimeout: NodeJS.Timeout | null = null;
    const handleUserActivity = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        updateActivity();
        activityTimeout = null;
      }, 5000); // actualizar máximo cada 5 segs
    };

    window.addEventListener('mousemove', handleUserActivity, { passive: true });
    window.addEventListener('keydown', handleUserActivity, { passive: true });
    window.addEventListener('click', handleUserActivity, { passive: true });
    window.addEventListener('touchstart', handleUserActivity, { passive: true });
    window.addEventListener('scroll', handleUserActivity, { passive: true });

    // Verificación periódica y al volver a la pestaña (visibilitychange/focus)
    const checkInactivity = async () => {
      const lastActStr = localStorage.getItem('logistrade_last_activity');
      if (!lastActStr) return;
      const lastAct = parseInt(lastActStr, 10);
      const isRemember = localStorage.getItem('logistrade_remember_me') === '1';
      // 5h inactivo sin "remember", 24h inactivo con "remember"
      const maxIdleMs = isRemember ? (24 * 60 * 60 * 1000) : (5 * 60 * 60 * 1000);
      const now = Date.now();

      if (now - lastAct > maxIdleMs) {
        // Expiró por inactividad: intentar refresh o expulsar
        const refreshed = await refreshSession();
        if (!refreshed) {
          onSessionExpired?.();
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkInactivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkInactivity);

    const interval = setInterval(checkInactivity, 60000); // Chequear cada minuto

    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkInactivity);
      clearInterval(interval);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, [user]);

  const login = (newUser: User) => {
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    localStorage.setItem('logistrade_last_activity', Date.now().toString());
    localStorage.removeItem('logistrade_session_expired');
    if (!canUseViewAs(newUser.roles)) {
      saveViewAsRole(null);
      setViewAsRoleState(null);
    }
    setUser(newUser);
  };

  const logout = () => {
    // Best-effort: le dice al servidor que revoque the session (borra las cookies) y Clears el
    // estado local ya mismo, sin esperar the response.
    originalFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('logistrade_current_view');
    localStorage.removeItem('logistrade_remember_me');
    localStorage.removeItem('logistrade_last_activity');
    saveViewAsRole(null);
    setViewAsRoleState(null);
    setUser(null);
  };

  // Updates campos propios (ej. teléfono de WhatsApp en Mi Perfil) sin forzar un re-login --
  // the backend ya persistió el cambio, esto solo sincroniza the status en memoria/localStorage.
  const updateUser = (patch: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;
    if (!canUseViewAs(user.roles) && viewAsRole) {
      saveViewAsRole(null);
      setViewAsRoleState(null);
    }
  }, [user, viewAsRole]);

  const effectiveRoles = useMemo(
    () => getEffectiveRoles(user?.roles, viewAsRole),
    [user?.roles, viewAsRole],
  );

  const hasRole = useCallback((role: UserRole | UserRole[]) => {
    if (!user) return false;
    return hasEffectiveRole(user.roles, viewAsRole, role);
  }, [user, viewAsRole]);

  const setViewAsRole = useCallback((role: UserRole | null) => {
    if (!user || !canUseViewAs(user.roles)) return;
    if (role !== null && !isPreviewableRole(role)) return;
    saveViewAsRole(role);
    setViewAsRoleState(role);
  }, [user]);

  const clearViewAsRole = useCallback(() => {
    saveViewAsRole(null);
    setViewAsRoleState(null);
  }, []);

  const canUseViewAsPreview = !!user && canUseViewAs(user.roles);
  const isViewingAs = canUseViewAsPreview && viewAsRole !== null;

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      updateUser,
      isAuthenticated: !!user,
      isCheckingSession,
      hasRole,
      effectiveRoles,
      viewAsRole,
      isViewingAs,
      canUseViewAs: canUseViewAsPreview,
      setViewAsRole,
      clearViewAsRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
